import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import API_CONFIG from '../config/api';

const MapSharingDialog = ({ isOpen, onClose, map, activeLayers, layerGroups, isLeftPanelOpen, leftOverlayOffset }) => {
  const { makeAuthenticatedRequest } = useAuth();
  const [shareType, setShareType] = useState('private');
  const [title, setTitle] = useState('');
  const [password, setPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [error, setError] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setPassword('');
      setShareResult(null);
      setError('');
    }
  }, [isOpen]);

  const handleShare = async () => {
    console.log('handleShare called');
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (shareType === 'private' && !password.trim()) {
      setError('Password is required for private maps');
      return;
    }

    if (!map || !activeLayers || activeLayers.length === 0) {
      setError('No active layers to share');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      // Calculate the visual center accounting for overlapping left panel
      const calculateVisualCenter = () => {
        const mapCanvas = map.getCanvas();
        const canvasRect = mapCanvas.getBoundingClientRect();
        
        // The left panel OVERLAPS the map canvas, changing the perceived center
        // The right panel does NOT overlap (it's separate)
        const leftPanelWidth =
          typeof leftOverlayOffset === 'number'
            ? leftOverlayOffset
            : (isLeftPanelOpen ? 398 : 48); // 48px nav rail + 350px sidebar when open
        
        // Calculate the center of the VISIBLE (non-overlapped) part of the map
        // Visible area starts after the left panel and goes to the right edge
        const visibleStartX = leftPanelWidth;
        const visibleEndX = canvasRect.width;
        const visibleWidth = visibleEndX - visibleStartX;
        const visibleCenterX = visibleStartX + (visibleWidth / 2);
        const visibleCenterY = canvasRect.height / 2;
        
        // Convert pixel coordinates to map coordinates
        const pixelPoint = [visibleCenterX, visibleCenterY];
        return map.unproject(pixelPoint);
      };

      // Get current map state with corrected center
      const center = calculateVisualCenter();
      const zoom = map.getZoom();
      const bearing = map.getBearing();
      const pitch = map.getPitch();
      const style = map.getStyle();

      // Get map style ID
      let mapStyleId = 'streets-v11';
      if (style && style.metadata && style.metadata['mapbox:origin']) {
        const styleMatch = style.metadata['mapbox:origin'].match(/mapbox:\/\/styles\/mapbox\/(.+)$/);
        if (styleMatch) {
          mapStyleId = styleMatch[1];
        }
      }

      // Helper function to get current layer styles from the map
      const getCurrentLayerStyles = (layerId, geometryType) => {
        const styleConfig = {};
        
        try {
          switch (geometryType?.toLowerCase()) {
            case 'point':
            case 'multipoint':
              const circleColor = map.getPaintProperty(layerId, 'circle-color');
              const circleRadius = map.getPaintProperty(layerId, 'circle-radius');
              const circleStrokeColor = map.getPaintProperty(layerId, 'circle-stroke-color');
              const circleStrokeWidth = map.getPaintProperty(layerId, 'circle-stroke-width');
              const circleOpacity = map.getPaintProperty(layerId, 'circle-opacity');
              
              if (circleColor) styleConfig['circle-color'] = circleColor;
              if (circleRadius) styleConfig['circle-radius'] = circleRadius;
              if (circleStrokeColor) styleConfig['circle-stroke-color'] = circleStrokeColor;
              if (circleStrokeWidth) styleConfig['circle-stroke-width'] = circleStrokeWidth;
              if (circleOpacity) styleConfig['circle-opacity'] = circleOpacity;
              break;
              
            case 'linestring':
            case 'multilinestring':
              const lineColor = map.getPaintProperty(layerId, 'line-color');
              const lineWidth = map.getPaintProperty(layerId, 'line-width');
              const lineOpacity = map.getPaintProperty(layerId, 'line-opacity');
              
              if (lineColor) styleConfig['line-color'] = lineColor;
              if (lineWidth) styleConfig['line-width'] = lineWidth;
              if (lineOpacity) styleConfig['line-opacity'] = lineOpacity;
              break;
              
            case 'polygon':
            case 'multipolygon':
              const fillColor = map.getPaintProperty(`${layerId}-fill`, 'fill-color');
              const fillOpacity = map.getPaintProperty(`${layerId}-fill`, 'fill-opacity');
              const strokeColor = map.getPaintProperty(`${layerId}-stroke`, 'line-color');
              const strokeWidth = map.getPaintProperty(`${layerId}-stroke`, 'line-width');
              const strokeOpacity = map.getPaintProperty(`${layerId}-stroke`, 'line-opacity');
              
              if (fillColor) styleConfig['fill-color'] = fillColor;
              if (fillOpacity) styleConfig['fill-opacity'] = fillOpacity;
              if (strokeColor) styleConfig['line-color'] = strokeColor;
              if (strokeWidth) styleConfig['line-width'] = strokeWidth;
              if (strokeOpacity) styleConfig['line-opacity'] = strokeOpacity;
              break;
              
            default:
              // For unknown types, try to get basic properties
              const basicColor = map.getPaintProperty(layerId, 'circle-color') || 
                                map.getPaintProperty(layerId, 'line-color') || 
                                map.getPaintProperty(layerId, 'fill-color');
              if (basicColor) {
                styleConfig[geometryType?.toLowerCase() === 'polygon' ? 'fill-color' : 'circle-color'] = basicColor;
              }
              break;
          }
        } catch (error) {
          console.warn(`Could not extract styles for layer ${layerId}:`, error);
        }
        
        return styleConfig;
      };

      // Helper function to get layer visibility and opacity
      const getLayerVisibilityAndOpacity = (layerId, geometryType) => {
        try {
          // Check main layer visibility
          let visibility = map.getLayoutProperty(layerId, 'visibility');
          
          // For polygons, check both fill and stroke layers
          if (geometryType?.toLowerCase().includes('polygon')) {
            const fillVisibility = map.getLayoutProperty(`${layerId}-fill`, 'visibility');
            const strokeVisibility = map.getLayoutProperty(`${layerId}-stroke`, 'visibility');
            visibility = fillVisibility || strokeVisibility || visibility;
          }
          
          const isVisible = visibility !== 'none';
          
          // Get opacity from the appropriate paint property
          let opacity = 1.0;
          if (geometryType?.toLowerCase().includes('point')) {
            opacity = map.getPaintProperty(layerId, 'circle-opacity') || 1.0;
          } else if (geometryType?.toLowerCase().includes('line')) {
            opacity = map.getPaintProperty(layerId, 'line-opacity') || 1.0;
          } else if (geometryType?.toLowerCase().includes('polygon')) {
            // For polygons, calculate the effective opacity from fill-opacity
            // The SharedMapViewer uses fill-opacity = 0.3 * opacity, so we need to reverse this
            const fillOpacity = map.getPaintProperty(`${layerId}-fill`, 'fill-opacity') || 0.3;
            opacity = fillOpacity / 0.3; // Convert back to the base opacity value
          }
          
          return { isVisible, opacity };
        } catch (error) {
          console.warn(`Could not get visibility/opacity for layer ${layerId}:`, error);
          return { isVisible: true, opacity: 1.0 };
        }
      };

      // Prepare layer configurations
      const layerConfigs = activeLayers.map((layer, index) => {
        // Find if layer is in a group
        let groupInfo = null;
        if (layerGroups) {
          for (const group of layerGroups) {
            const layerInGroup = group.layers.find(l => l.id === layer.id);
            if (layerInGroup) {
              groupInfo = {
                group_id: group.id,
                group_name: group.name,
                group_order: 0 // TODO: Implement group ordering
              };
              break;
            }
          }
        }

        // Get current styles and visibility from the map
        const layerId = `layer-${layer.id}`;
        const geometryType = layer.geometry_type;
        console.log(`Extracting styles for layer ${layerId}, geometry: ${geometryType}`);
        const styleConfig = getCurrentLayerStyles(layerId, geometryType);
        const { isVisible, opacity } = getLayerVisibilityAndOpacity(layerId, geometryType);
        console.log(`Layer ${layerId} - styleConfig:`, styleConfig, 'visibility:', isVisible, 'opacity:', opacity);

        return {
          dataset_id: layer.id,
          layer_order: index,
          is_visible: isVisible,
          opacity: opacity,
          style_config: styleConfig,
          label_config: {}, // TODO: Get actual label config if implemented
          ...groupInfo
        };
      });

      // Prepare request data
      const requestData = {
        title: title.trim(),
        share_type: shareType,
        map_state: {
          center_lng: center.lng,
          center_lat: center.lat,
          zoom: zoom,
          bearing: bearing,
          pitch: pitch,
          style: mapStyleId
        },
        layer_configs: layerConfigs,
        password: shareType === 'private' ? password.trim() : null
      };

      // Make API request
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/maps/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create shared map');
      }

      const result = await response.json();
      setShareResult(result);

    } catch (err) {
      console.error('Error creating shared map:', err);
      setError(err.message || 'Failed to create shared map');
    } finally {
      setIsCreating(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareResult) return;

    const fullUrl = `${window.location.origin}${shareResult.share_url}`;
    
    try {
      await navigator.clipboard.writeText(fullUrl);
      // TODO: Show success notification
      console.log('Share URL copied to clipboard');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleClose = () => {
    setShareResult(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  console.log('MapSharingDialog rendering, isOpen:', isOpen);

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content map-sharing-dialog" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 Share Map</h2>
          <button className="modal-close" onClick={handleClose}>✕</button>
        </div>

        <div className="modal-body">
          {shareResult ? (
            // Success view
            <div className="share-success">
              <div className="success-icon">✅</div>
              <h3>Map Shared Successfully!</h3>
              <p>Your map "{shareResult.title}" has been shared.</p>
              
              <div className="share-url-container">
                <label>Share URL:</label>
                <div className="share-url-display">
                  <input 
                    type="text" 
                    value={`${window.location.origin}${shareResult.share_url}`}
                    readOnly
                    className="share-url-input"
                  />
                  <button 
                    className="btn-copy-url"
                    onClick={copyShareUrl}
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="share-details">
                <div className="share-detail">
                  <span className="detail-label">Share Type:</span>
                  <span className="detail-value">{shareResult.share_type}</span>
                </div>
                <div className="share-detail">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">{new Date(shareResult.created_at).toLocaleString()}</span>
                </div>
                {shareResult.expires_at && (
                  <div className="share-detail">
                    <span className="detail-label">Expires:</span>
                    <span className="detail-value">{new Date(shareResult.expires_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Form view
            <div className="share-form">
              <div className="form-section">
                
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter a title for your shared map"
                    className="form-input"
                  />
                </div>

              </div>

              <div className="form-section">
                
                <div className="form-group">
                  <label>Share Type</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        value="public"
                        checked={shareType === 'public'}
                        onChange={(e) => setShareType(e.target.value)}
                      />
                      <span className="radio-label">
                        <strong>Public</strong> - Anyone with the link can view
                      </span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        value="private"
                        checked={shareType === 'private'}
                        onChange={(e) => setShareType(e.target.value)}
                      />
                      <span className="radio-label">
                        <strong>Private</strong> - Protected by password
                      </span>
                    </label>
                  </div>
                </div>

                {shareType === 'private' && (
                  <div className="private-settings">
                    <div className="form-group">
                      <label>Password *</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password for map access"
                        className="form-input"
                      />
                      <small className="form-help">
                        Anyone with the link and this password will be able to view the map
                      </small>
                    </div>
                  </div>
                )}

              </div>

              <div className="form-section">
                <h3>What will be shared?</h3>
                <div className="share-preview">
                  <div className="preview-item">
                    <span className="preview-icon">🗺️</span>
                    <span>Current map view and style</span>
                  </div>
                  <div className="preview-item">
                    <span className="preview-icon">📊</span>
                    <span>{activeLayers.length} active layers with styling</span>
                  </div>
                  <div className="preview-item">
                    <span className="preview-icon">👁️</span>
                    <span>Read-only access (no editing)</span>
                  </div>
                  <div className="preview-item">
                    <span className="preview-icon">🔒</span>
                    <span>{shareType === 'private' ? 'Protected by password' : 'No authentication required'}</span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {shareResult ? (
            <button className="btn-primary" onClick={handleClose}>
              Close
            </button>
          ) : (
            <>
              <button className="btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleShare}
                disabled={isCreating || !title.trim()}
              >
                {isCreating ? 'Creating...' : 'Create Share Link'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapSharingDialog;
